import { styled, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { FormField } from '../../../../components/FormField/FormField';
import { Multiselect } from '../../../../components/Multiselect/Multiselect';
import { Switch } from '../../../../components/SwitchButton/Switch';
import { AvatarCell } from '../../../../components/Table/CellRenderers/AvatarCell';
import { Typography } from '../../../../components/Typography/Typography';

import { SelectItem } from '../../../../types';
import { isSubmitter } from '../../../../utils/roles';
import { IUserFileds } from './UserModal';
import { UpdateUserSkeletonLoader } from '../../../../components/SkeletonLoader/UpdateUser.SkeletonLoader';
import { useUsers } from '../../../../queries/hooks/useUsers';

export function ProxyFields() {
  const { colors } = useTheme();
  const { isLoading: areUsersLoading, data: users } = useUsers();

  const { setFieldValue, values, touched, errors, setFieldTouched } =
    useFormikContext<IUserFileds>();
  const handleError = (field) => (touched[field] ? errors[field] : '');

  // SVP-826: the mirror of the Proxy picker in the Submitter section — assigning yourself here would
  // make you your own proxy just as surely, only from the other side of the same relation.
  const operatingPartnersOptions = useMemo(() => {
    if (!users) return [];
    return users
      .filter((user) => isSubmitter(user) && user.email !== values.email)
      .map((user) => ({
        id: user.id,
        value: user.name,
      }));
  }, [users, values.email]);

  const selectedSubmitters = useMemo(
    () => (values?.proxyOperatingPartners ?? []) as SelectItem[],
    [values?.proxyOperatingPartners]
  );

  const labelStyle = {
    color: colors.text.caption,
  };

  return areUsersLoading ? (
    <UpdateUserSkeletonLoader />
  ) : (
    <>
      <FormField
        label={'Submitters (Optional)'}
        error={handleError('proxyOperatingPartners')}
        labelStyle={labelStyle}
      >
        <Multiselect
          listboxStyle={{ height: '195px' }}
          options={operatingPartnersOptions}
          value={selectedSubmitters}
          onChange={(_, selected) => {
            setFieldValue('proxyOperatingPartners', selected);
            // SVP-820: the permission list is a subset of the assignment, so dropping a submitter here
            // has to drop their permission too — otherwise removing and re-adding them would silently
            // bring back a switch the admin had already turned off.
            const selectedIds = (selected ?? []).map((item) => Number(item.id));
            setFieldValue(
              'proxyCanSubmitFor',
              (values?.proxyCanSubmitFor ?? []).filter((id) => selectedIds.includes(id))
            );
          }}
          onBlur={() => {
            setFieldTouched('proxyOperatingPartners');
          }}
          disablePortal
          fieldPlaceholder='Select submitters'
        />
      </FormField>
      {selectedSubmitters.length > 0 && (
        <PermissionsList>
          <PermissionRow>
            <Typography variant='caption' color={colors.text.caption}>
              Submitter
            </Typography>
            <SwitchCell>
              <Typography variant='caption' color={colors.text.caption}>
                Allow submitting timesheets
              </Typography>
            </SwitchCell>
          </PermissionRow>
          {selectedSubmitters.map((submitter) => {
            const submitterId = Number(submitter.id);
            const canSubmit = (values?.proxyCanSubmitFor ?? []).includes(submitterId);
            return (
              <PermissionRow key={submitterId}>
                <AvatarCell
                  name={String(submitter.value ?? '')}
                  avatarBoxStyle={{ background: colors.surfaceIndicator.needsReview }}
                />
                <SwitchCell>
                  <Switch
                    checked={canSubmit}
                    onChange={() => {
                      const current = values?.proxyCanSubmitFor ?? [];
                      setFieldValue(
                        'proxyCanSubmitFor',
                        canSubmit
                          ? current.filter((id) => id !== submitterId)
                          : [...current, submitterId]
                      );
                    }}
                  />
                </SwitchCell>
              </PermissionRow>
            );
          })}
        </PermissionsList>
      )}
    </>
  );
}

const PermissionsList = styled('div')`
  display: flex;
  flex-direction: column;
`;

/** Only the lines that separate rows — no outer frame, matching the design. */
const PermissionRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 180px;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  }
`;

/** Header label and switches share the same right edge, flush with the list. */
const SwitchCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
